/**
 * Punkt 101 Stufe 2 — der Drawer „Saison-Kampagne anlegen“ (Mockup b, freigegeben 25.09.2026).
 *
 * Vier Schritte: Paket aus dem Katalog, Stichtag, Mail-Text mit Platzhaltern, Vorschau (Deutsch/
 * Englisch). Die Vorschau rendert der Server (`POST /api/saison-kampagnen/vorschau`) — dieselbe
 * Funktion, die auch der Stichtag-Lauf benutzt, damit hier genau das steht, was beim Gast ankommt.
 * Menue und Preis kommen immer aus dem Paket. Die englische Fassung wird beim ersten Öffnen einmal
 * per KI übersetzt und ist danach hier änderbar; Beträge kommen nie von der KI (Server prüft).
 * Nichts wird hier verschickt — außer der Testmail an den eingeloggten Betreiber.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { easterSunday } from "@maestro/feiertage";
import { apiMutate, useApi } from "../hooks/useApi";
import { money } from "./format";
import { Button, Drawer, Icon, Input, Textarea, DateInput, Radio } from "./components";
import { insertAtCursor } from "./TextbausteinPicker";

export interface SaisonKampagne {
  id: string;
  anlass: string;
  jahr: number;
  packageId: string | null;
  paketName: string | null;
  preisProPersonCents: number | null;
  stichtag: string;
  betreff: string;
  text: string;
  betreffEn: string | null;
  textEn: string | null;
  vormerkungenAktiv: number;
  offen: number;
  freigegeben: number;
  versendet: number;
  /** Punkt 101 Stufe 3: „Vormerkung → Buchung“ (Basis: angeschrieben). */
  kennzahl?: { angeschrieben: number; geantwortet: number; anfragen: number; gebucht: number; quoteProzent: number | null };
  /** Antoine 26.09.2026: erste Kampagne einzeln, danach Stichprobe + Sammelfreigabe. */
  freigabe?: { ersteKampagne: boolean; stichprobe: number; geprueft: number; sammelErlaubt: boolean };
  /** Offene Vorschläge: Sprache und Nachzügler. */
  vorschlaege?: { id: string; sprache: "de" | "en"; nachzuegler: boolean }[];
}

/** Ab so vielen Personen ist es eine Gruppe (MAESTRO), darunter reserviert der Gast über OpenTable. */
export const GRUPPE_AB = 10;

interface PaketZeile {
  id: string;
  name: string;
  description: string | null;
  kind: string;
  pricePerPersonCents: number;
  courseCount: number | null;
  archivedAt?: string | null;
}

const ANLASS_NAME: Record<string, string> = { valentinstag: "Valentinstag", ostermontag: "Ostermontag", weihnachten: "Weihnachten", silvester: "Silvester" };
export const anlassName = (anlass: string) => ANLASS_NAME[anlass] ?? anlass;

/**
 * Entscheidung 3 (26.09.2026): feste Zeitpunkte in WOCHEN vor dem Anlass als Standard, je Kampagne
 * änderbar. Die Werte treffen die bisherigen Stichtage (Weihnachten/Silvester Mitte Oktober,
 * Valentinstag Mitte Januar, Ostern 6 Wochen vorher).
 */
export const WOCHEN_VORHER: Record<string, number> = { weihnachten: 10, silvester: 11, valentinstag: 4, ostermontag: 6 };

export function anlassTag(anlass: string, jahr: number): string {
  if (anlass === "valentinstag") return `${jahr}-02-14`;
  if (anlass === "weihnachten") return `${jahr}-12-24`;
  if (anlass === "ostermontag") {
    const d = easterSunday(jahr);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
  }
  return `${jahr}-12-31`;
}

export function stichtagVorschlag(anlass: string, jahr: number): string {
  const d = new Date(`${anlassTag(anlass, jahr)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 7 * (WOCHEN_VORHER[anlass] ?? 8));
  return d.toISOString().slice(0, 10);
}

export const tagMonat = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;

const PLATZHALTER = ["{{paket}}", "{{preis_pro_person}}", "{{menue}}", "{{anlass}}", "{{datum}}", "{{betrieb}}"];
const BETREFF_VORGABE = "Unser {{paket}} für {{anlass}} – jetzt Tisch sichern";
const TEXT_VORGABE = `Guten Tag,

Sie haben sich bei uns für {{anlass}} vorgemerkt – schön, dass Sie dabei sein möchten!

Für den {{datum}} haben wir unser {{paket}} vorbereitet, für {{preis_pro_person}} pro Person:
{{menue}}

Sichern Sie sich Ihren Tisch gleich über den Knopf unten – oder fragen Sie als Gruppe bei uns an.

Herzliche Grüße
{{betrieb}}`;

function Schritt({ n, titel, children, rechts }: { n: number; titel: string; children: React.ReactNode; rechts?: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2 border-t border-[#F2ECDF] pt-4 first:border-t-0 first:pt-0 dark:border-white/[0.06]">
      <h5 className="flex items-center gap-2 font-label-md text-[13px] font-bold text-on-background dark:text-inverse-on-surface">
        <span className="inline-flex size-5 items-center justify-center rounded-full bg-primary/10 text-[11px] text-primary">{n}</span>
        {titel}
        {rechts && <span className="ml-auto">{rechts}</span>}
      </h5>
      {children}
    </section>
  );
}

const HINWEIS = "font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim";

export function SaisonKampagneDrawer({
  anlass,
  jahr,
  kampagne,
  anzahl,
  onClose,
  onGespeichert,
}: {
  anlass: string;
  jahr: number;
  kampagne: SaisonKampagne | null;
  anzahl: { aktiv: number; de: number; en: number };
  onClose: () => void;
  onGespeichert: () => void;
}) {
  const paketeQ = useApi<PaketZeile[]>("/api/packages");
  // Absender (Entscheidung 8) und OpenTable-Link (Knopf „Tisch reservieren“) aus den Einstellungen.
  const einstellungenQ = useApi<{ emailSenderName?: string | null; opentableUrl?: string | null }>("/api/settings/payment-terms");
  const absender = einstellungenQ.data?.emailSenderName?.trim() || null;
  const opentable = einstellungenQ.data?.opentableUrl?.trim() || null;
  const pakete = useMemo(() => (paketeQ.data ?? []).filter((p) => p.archivedAt == null && p.kind !== "flat" && p.pricePerPersonCents > 0), [paketeQ.data]);

  const [packageId, setPackageId] = useState<string>(kampagne?.packageId ?? "");
  const [stichtag, setStichtag] = useState(kampagne?.stichtag ?? stichtagVorschlag(anlass, jahr));
  const [betreff, setBetreff] = useState(kampagne?.betreff ?? BETREFF_VORGABE);
  const [text, setText] = useState(kampagne?.text ?? TEXT_VORGABE);
  const [betreffEn, setBetreffEn] = useState(kampagne?.betreffEn ?? "");
  const [textEn, setTextEn] = useState(kampagne?.textEn ?? "");
  const [sprache, setSprache] = useState<"de" | "en">("de");
  const [vorschau, setVorschau] = useState<{ html: string; empfaenger: string | null } | null>(null);
  const [meldung, setMeldung] = useState<{ ton: "gut" | "fehler"; text: string } | null>(null);
  const [laeuft, setLaeuft] = useState<null | "speichern" | "testmail" | "uebersetzen">(null);
  const textRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!packageId && pakete.length > 0) setPackageId(pakete[0]!.id);
  }, [pakete, packageId]);

  const aktBetreff = sprache === "en" ? betreffEn : betreff;
  const aktText = sprache === "en" ? textEn : text;

  // Vorschau vom Server, leicht verzögert beim Tippen.
  useEffect(() => {
    if (!packageId || !aktBetreff.trim() || !aktText.trim()) return;
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      apiMutate<{ html: string; empfaenger: string | null }>(
        "/api/saison-kampagnen/vorschau",
        "POST",
        { anlass, jahr, packageId, betreff: aktBetreff, text: aktText, sprache },
        ctrl.signal,
      )
        .then(setVorschau)
        .catch((e: unknown) => {
          if (!ctrl.signal.aborted) setMeldung({ ton: "fehler", text: e instanceof Error ? e.message : String(e) });
        });
    }, 350);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [anlass, jahr, packageId, aktBetreff, aktText, sprache]);

  async function uebersetzen() {
    setLaeuft("uebersetzen");
    try {
      const r = await apiMutate<{ betreffEn: string; textEn: string }>("/api/saison-kampagnen/uebersetzen", "POST", { betreff, text });
      setBetreffEn(r.betreffEn);
      setTextEn(r.textEn);
    } catch (e) {
      setMeldung({ ton: "fehler", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLaeuft(null);
    }
  }

  function zeigeSprache(s: "de" | "en") {
    setSprache(s);
    if (s === "en" && !textEn.trim() && laeuft !== "uebersetzen") void uebersetzen();
  }

  async function speichern() {
    setLaeuft("speichern");
    setMeldung(null);
    const felder = { packageId, stichtag, betreff, text, betreffEn: betreffEn.trim() || null, textEn: textEn.trim() || null };
    try {
      if (kampagne) await apiMutate(`/api/saison-kampagnen/${kampagne.id}`, "PATCH", felder);
      else await apiMutate("/api/saison-kampagnen", "POST", { anlass, jahr, ...felder });
      onGespeichert();
    } catch (e) {
      setMeldung({ ton: "fehler", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLaeuft(null);
    }
  }

  async function testmail() {
    setLaeuft("testmail");
    setMeldung(null);
    try {
      const r = await apiMutate<{ sentTo: string }>("/api/saison-kampagnen/testmail", "POST", { anlass, jahr, packageId, betreff: aktBetreff, text: aktText, sprache });
      setMeldung({ ton: "gut", text: `Testmail an ${r.sentTo} verschickt.` });
    } catch (e) {
      setMeldung({ ton: "fehler", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLaeuft(null);
    }
  }

  const titel = `Saison-Kampagne ${anlassName(anlass)} ${jahr}`;
  // Entscheidung 7: wer sich auf Englisch vorgemerkt hat, bekommt eine geprüfte englische Fassung.
  const enFehlt = anzahl.en > 0 && (!betreffEn.trim() || !textEn.trim());
  const bereit = Boolean(packageId && stichtag && betreff.trim() && text.trim()) && !enFehlt;

  return (
    <Drawer
      open
      onClose={onClose}
      breite="breit"
      title={
        <span className="flex items-center gap-2">
          <Icon name="campaign" className="text-[20px] text-primary" /> {titel}
        </span>
      }
      subtitle={`Einmal pro Saison anlegen. Am Stichtag bereitet MAESTRO für jede ${anlassName(anlass)}-Vormerkung eine Mail vor.`}
      footer={
        <div className="flex w-full flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={onClose} disabled={laeuft !== null}>
            Abbrechen
          </Button>
          <Button variant="ghost" className="ml-auto" onClick={() => void testmail()} disabled={!bereit || laeuft !== null}>
            {laeuft === "testmail" ? "Sendet …" : "Testmail an mich"}
          </Button>
          <Button variant="primary" onClick={() => void speichern()} disabled={!bereit || laeuft !== null}>
            <Icon name="check" className="text-[16px]" /> {laeuft === "speichern" ? "Speichert …" : "Kampagne speichern"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex gap-3 rounded-xl border border-secondary/30 bg-secondary/[0.06] px-4 py-3">
          <Icon name="verified_user" className="mt-0.5 shrink-0 text-[18px] text-secondary dark:text-[#b8ae9e]" />
          <p className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
            <b>Es wird nichts automatisch verschickt.</b> Am Stichtag liegen die Vorschläge unter „Freigaben“ bereit. Erst Ihr Klick schickt sie ab,
            alle auf einmal oder einzeln.
          </p>
        </div>

        {meldung && (
          <p role="status" className={`rounded-lg px-3 py-2 font-body-sm text-body-sm ${meldung.ton === "gut" ? "bg-secondary/[0.08]" : "bg-error/[0.08] text-error dark:text-[#e39b92]"}`}>
            {meldung.text}
          </p>
        )}

        <Schritt n={1} titel="Paket aus dem Katalog">
          {pakete.length === 0 && !paketeQ.loading ? (
            <p className={HINWEIS}>Im Katalog gibt es noch kein aktives Paket mit Preis je Person.</p>
          ) : (
            <div className="flex flex-col gap-2" role="radiogroup" aria-label="Paket">
              {pakete.map((p) => (
                <label
                  key={p.id}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-2.5 ${
                    packageId === p.id ? "border-primary bg-primary/[0.05]" : "border-[#EFE8DA] dark:border-white/[0.08]"
                  }`}
                >
                  <Radio name="saison-paket" checked={packageId === p.id} onChange={() => setPackageId(p.id)} />
                  <span className="min-w-0 flex-1">
                    <span className="block font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{p.name}</span>
                    <span className={HINWEIS}>{p.description || (p.courseCount ? `Menü · ${p.courseCount} Gänge` : "Menü")}</span>
                  </span>
                  <span className="shrink-0 font-body-md text-body-md font-semibold tabular-nums">{money(p.pricePerPersonCents)} p. P.</span>
                </label>
              ))}
            </div>
          )}
          <p className={HINWEIS}>Menü und Preis kommen immer aus dem Paket. Ändert sich der Preis im Katalog, gilt der Stand am Stichtag.</p>
        </Schritt>

        <Schritt n={2} titel="Wann vorbereiten?">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="font-body-sm text-body-sm font-semibold">Stichtag</span>
              <DateInput value={stichtag} onValueChange={(v) => setStichtag(v)} />
              <span className={HINWEIS}>
                Standard: {WOCHEN_VORHER[anlass] ?? 8} Wochen vor {anlassName(anlass)} ({tagMonat(stichtagVorschlag(anlass, jahr))}). Hier änderbar.
              </span>
            </label>
            <div className="flex flex-col gap-1">
              <span className="font-body-sm text-body-sm font-semibold">An wen?</span>
              <span className="font-body-md text-body-md">
                <b>{anzahl.aktiv}</b> {anlassName(anlass)}-Vormerkungen
              </span>
              <span className={HINWEIS}>Nur wer sich für {anlassName(anlass)} vorgemerkt hat. Abgemeldete nie.</span>
            </div>
          </div>
        </Schritt>

        <Schritt n={3} titel="Mail-Text">
          <label className="flex flex-col gap-1">
            <span className="font-body-sm text-body-sm font-semibold">Betreff</span>
            <Input value={betreff} maxLength={300} onChange={(e) => setBetreff(e.target.value)} />
          </label>
          <span className="font-body-sm text-body-sm font-semibold">Text</span>
          <div className="flex flex-wrap gap-1.5">
            {PLATZHALTER.map((p) => (
              <button
                key={p}
                type="button"
                className="rounded-md border border-[#E3DBCB] bg-[#FBF6EC] px-2 py-0.5 font-mono text-[11.5px] text-primary dark:border-white/[0.12] dark:bg-white/[0.04]"
                onClick={() => insertAtCursor(textRef.current, text, setText, p)}
              >
                {p}
              </button>
            ))}
          </div>
          <Textarea ref={textRef} className="min-h-[220px] resize-y" maxLength={6000} value={text} onChange={(e) => setText(e.target.value)} aria-label="Text" />
          <p className={HINWEIS}>Klick auf ein Feld setzt es an der Schreibstelle ein. Abmeldelink und Impressum hängt MAESTRO immer selbst an, sie lassen sich nicht entfernen.</p>
          <div data-testid="saison-knoepfe" className="flex flex-col gap-1.5 rounded-xl border border-[#EFE8DA] px-3.5 py-3 dark:border-white/[0.08]">
            <span className="font-body-sm text-body-sm font-semibold">Zwei Knöpfe unter jeder Mail</span>
            <span className="flex items-start gap-2 font-body-sm text-body-sm">
              <Icon name="table_restaurant" className="mt-0.5 text-[16px] text-primary" />
              <span>
                <b>Tisch reservieren</b> (bis {GRUPPE_AB - 1} Personen) — {opentable ? <span className="break-all text-secondary">{opentable}</span> : <span className="text-error">kein OpenTable-Link hinterlegt (Einstellungen → Firma), der Knopf fehlt dann</span>}
              </span>
            </span>
            <span className="flex items-start gap-2 font-body-sm text-body-sm">
              <Icon name="groups" className="mt-0.5 text-[16px] text-primary" />
              <span>
                <b>Als Gruppe anfragen</b> (ab {GRUPPE_AB} Personen) — daraus wird eine Anfrage in MAESTRO, wie über das Formular.
              </span>
            </span>
            <span className={HINWEIS}>Absender: {absender ?? "Name des Betriebs"} (Einstellungen → E-Mail).</span>
          </div>
        </Schritt>

        <Schritt
          n={4}
          titel="Vorschau"
          rechts={
            <span className="inline-flex overflow-hidden rounded-lg border border-[#E3DBCB] text-[12px] dark:border-white/[0.12]" role="group" aria-label="Sprache der Vorschau">
              {(["de", "en"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  aria-pressed={sprache === s}
                  onClick={() => zeigeSprache(s)}
                  className={`px-2.5 py-1 ${sprache === s ? "bg-on-background text-inverse-on-surface dark:bg-inverse-on-surface dark:text-inverse-surface" : ""}`}
                >
                  {s === "de" ? `Deutsch (${anzahl.de})` : `Englisch (${anzahl.en})`}
                </button>
              ))}
            </span>
          }
        >
          {enFehlt && (
            <p role="status" className="rounded-lg bg-secondary/[0.08] px-3 py-2 font-body-sm text-body-sm">
              {`${anzahl.en} ${anzahl.en === 1 ? "Gast hat" : "Gäste haben"} sich auf Englisch vorgemerkt. Bitte die englische Fassung ansehen und prüfen, bevor Sie speichern.`}
            </p>
          )}
          {sprache === "en" && (
            <div className="flex flex-col gap-2">
              {laeuft === "uebersetzen" ? (
                <p className={HINWEIS}>Übersetze einmalig ins Englische …</p>
              ) : (
                <>
                  <Input value={betreffEn} maxLength={300} onChange={(e) => setBetreffEn(e.target.value)} aria-label="Betreff (Englisch)" />
                  <Textarea className="min-h-[160px] resize-y" maxLength={6000} value={textEn} onChange={(e) => setTextEn(e.target.value)} aria-label="Text (Englisch)" />
                  <p className={HINWEIS}>
                    Die englische Fassung wurde einmal übersetzt und lässt sich hier ändern. Preis und Menü kommen aus dem Paket.{" "}
                    <button type="button" className="text-primary underline-offset-2 hover:underline" onClick={() => void uebersetzen()}>
                      Neu übersetzen
                    </button>
                  </p>
                </>
              )}
            </div>
          )}
          {vorschau && (
            <>
              <iframe
                title="Vorschau der Mail"
                srcDoc={vorschau.html}
                sandbox=""
                referrerPolicy="no-referrer"
                className="h-[460px] w-full rounded-lg border border-[#F0E7D6] bg-white dark:border-white/[0.06]"
              />
              <p className={HINWEIS}>
                {vorschau.empfaenger ? `Vorschau mit einer echten Vormerkung (${vorschau.empfaenger}). ` : ""}So kommt die Mail beim Gast an.
              </p>
            </>
          )}
        </Schritt>
      </div>
    </Drawer>
  );
}
